import React from 'react';
import ReactMarkdown from 'react-markdown';
import { Sparkle, TrendUp } from '@phosphor-icons/react';

const MARKDOWN_COMPONENTS = {
  p: ({ children }) => <p className="mb-3">{children}</p>,
  strong: ({ children }) => <strong className="text-white font-semibold">{children}</strong>,
  em: ({ children }) => <em className="text-[#FBBF24] not-italic">{children}</em>,
  ul: ({ children }) => <ul className="list-none space-y-1.5 mb-3">{children}</ul>,
  li: ({ children }) => (
    <li className="flex items-start gap-2">
      <span className="text-[#A855F7] mt-1">▸</span>
      <span className="flex-1">{children}</span>
    </li>
  ),
  h1: ({ children }) => <h4 className="text-white font-heading font-bold text-base mb-2 mt-3">{children}</h4>,
  h2: ({ children }) => <h4 className="text-white font-heading font-bold text-base mb-2 mt-3">{children}</h4>,
  h3: ({ children }) => <h5 className="text-white font-heading font-bold text-sm mb-1.5 mt-2">{children}</h5>,
  code: ({ children }) => (
    <code className="bg-[#0A0B0E] px-1.5 py-0.5 text-[#00FF66] font-mono text-xs">{children}</code>
  ),
};

const StreamingIdeaResult = React.forwardRef(function StreamingIdeaResult(
  { tradeIdea, isGenerating, ideaError },
  ref
) {
  if (!tradeIdea && !isGenerating && !ideaError) return null;

  return (
    <div ref={ref} className="bg-[#12141A] border border-[#A855F7]/30 p-6" data-testid="ai-trade-idea-result">
      <Header isGenerating={isGenerating} />
      {ideaError ? (
        <div className="text-sm text-[#FF3366]" data-testid="ai-idea-error">{ideaError}</div>
      ) : (
        <Body tradeIdea={tradeIdea} isGenerating={isGenerating} />
      )}
    </div>
  );
});

function Header({ isGenerating }) {
  return (
    <div className="flex items-center gap-2 mb-4">
      {isGenerating ? (
        <Sparkle size={24} weight="fill" className="text-[#A855F7] animate-pulse" />
      ) : (
        <TrendUp size={24} weight="duotone" className="text-[#A855F7]" />
      )}
      <h3 className="text-xl font-heading font-bold text-white">
        {isGenerating ? 'Generating trade idea...' : 'AI-Generated Trade Idea'}
      </h3>
      {isGenerating && <span className="text-xs font-mono text-[#94A3B8] ml-2">Streaming via GPT-5.2</span>}
    </div>
  );
}

function Body({ tradeIdea, isGenerating }) {
  return (
    <div className="text-sm text-[#94A3B8] leading-relaxed min-h-[80px] ai-markdown">
      {tradeIdea && <ReactMarkdown components={MARKDOWN_COMPONENTS}>{tradeIdea}</ReactMarkdown>}
      {!tradeIdea && isGenerating && (
        <span className="text-[#64748B] italic">Analyzing market conditions...</span>
      )}
      {isGenerating && tradeIdea && (
        <span className="inline-block w-2 h-4 bg-[#A855F7] animate-pulse ml-0.5" />
      )}
    </div>
  );
}

export default StreamingIdeaResult;
